import React, { useCallback, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Block, ParagraphBlockData, ParagraphFragment, ListStyle } from '../../../config';
import { stashCaret, stashCaretOffset, takeCaret, peekCaret, setCaretOffset, getCaretOffset } from '../../../utils/caretRestore';
import { normaliseHtml } from '../../../utils/htmlSlice';
import './paragraph-block.scss';

// Parsed block from pasted content
interface ParsedBlock {
  type: 'paragraph' | 'bullet_list' | 'heading';
  content: string;
  level?: number; // For headings
  /** For lists: pasted "1." lines make a numbered list. */
  listStyle?: ListStyle;
}

interface ParagraphBlockProps {
  block: Block;
  onChange: (data: ParagraphBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
  // When the paragraph continues on another page, this instance shows only one
  // piece of it. `block.data.content` is then that piece's HTML; the canvas
  // merges edits back into the whole paragraph.
  fragment?: ParagraphFragment;
  // Slash command props
  onSlashCommand?: (position: { top: number; left: number }) => void;
  onSlashSearch?: (query: string) => void;
  onSlashClose?: () => void;
  slashSearchQuery?: string;
  isSlashMenuOpen?: boolean;
  // Focus/interaction props
  onFocus?: () => void;
  onBlur?: () => void;
  // Enter splits the paragraph at the caret: `currentContent` stays in this
  // block, `afterContent` (possibly empty) becomes the new block below.
  onEnter?: (currentContent: string, afterContent?: string) => void;
  onBackspaceEmpty?: () => void;
  // Smart paste - create blocks from pasted content. `currentContent` is this
  // block's latest HTML so the caller can save it in the same update.
  onPasteMultiBlock?: (blocks: ParsedBlock[], currentContent: string) => void;
}

// How long after the last keystroke the DOM content is pushed into state.
// Short enough that the page reflows as you type; long enough not to re-render
// on every character.
const COMMIT_DELAY_MS = 120;

// Helper to detect bullet patterns
const BULLET_PATTERNS = [
  /^[•·]\s+/,      // • or · bullet
  /^[-–—]\s+/,     // dash variants
  /^\*\s+/,        // asterisk
  /^>\s+/,         // quote style
];

const NUMBERED_PATTERN = /^\d+[.)]\s+/; // 1. or 1)

// Parse pasted text into blocks
const parsePastedText = (text: string): ParsedBlock[] => {
  const lines = text.split('\n').filter(line => line.trim());
  const blocks: ParsedBlock[] = [];
  let currentBulletItems: string[] = [];
  // A run of "1." lines pastes as a numbered list; the run's first line
  // decides, so a run that mixes markers keeps one style.
  let currentListStyle: ListStyle = 'bullet';

  const flushBulletItems = () => {
    if (currentBulletItems.length > 0) {
      blocks.push({
        type: 'bullet_list',
        content: currentBulletItems.join('\n'),
        listStyle: currentListStyle,
      });
      currentBulletItems = [];
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check for bullet patterns
    let isBullet = false;
    let bulletContent = trimmedLine;

    for (const pattern of BULLET_PATTERNS) {
      if (pattern.test(trimmedLine)) {
        isBullet = true;
        bulletContent = trimmedLine.replace(pattern, '');
        break;
      }
    }

    // Check for numbered list
    let isNumbered = false;
    if (!isBullet && NUMBERED_PATTERN.test(trimmedLine)) {
      isBullet = true;
      isNumbered = true;
      bulletContent = trimmedLine.replace(NUMBERED_PATTERN, '');
    }

    if (isBullet) {
      if (currentBulletItems.length === 0) currentListStyle = isNumbered ? 'number' : 'bullet';
      currentBulletItems.push(bulletContent);
    } else {
      // Flush any pending bullet items
      flushBulletItems();

      // Regular paragraph
      if (trimmedLine) {
        blocks.push({
          type: 'paragraph',
          content: trimmedLine
        });
      }
    }
  }

  // Flush remaining bullet items
  flushBulletItems();

  return blocks;
};

// Serialise the contents of a range as HTML
const rangeToHtml = (range: Range): string => {
  const container = document.createElement('div');
  container.appendChild(range.cloneContents());
  return container.innerHTML;
};

// Contenteditable leaves a trailing <br> behind; it has no meaning for us
const trimTrailingBreak = (html: string): string => html.replace(/(<br\s*\/?>)+$/i, '');

// Focus a sibling fragment of the same paragraph (on another page) and place
// the caret at its start or end.
const focusSiblingFragment = (blockId: string, part: number, atEnd: boolean): boolean => {
  const el = document.querySelector(
    `[data-block-id="${blockId}"][data-fragment-part="${part}"] .paragraph-content`
  ) as HTMLElement | null;
  if (!el) return false;
  el.focus();
  setCaretOffset(el, atEnd ? (el.textContent?.length ?? 0) : 0);
  return true;
};

const ParagraphBlock: React.FC<ParagraphBlockProps> = ({
  block,
  onChange,
  isEditing,
  fragment,
  onSlashCommand,
  onSlashSearch,
  onSlashClose,
  slashSearchQuery = '',
  isSlashMenuOpen = false,
  onFocus,
  onBlur,
  onEnter,
  onBackspaceEmpty,
  onPasteMultiBlock
}) => {
  const data = block.data as ParagraphBlockData;
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isInSlashMode, setIsInSlashMode] = useState(false);

  // Track the last content we set to the DOM to avoid resetting user's typing
  const lastSetContentRef = useRef<string>('');
  const blockIdRef = useRef<string>('');
  const initializedRef = useRef(false);
  // Latest props for use inside timers (avoids stale closures)
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  const isSlashMenuOpenRef = useRef(isSlashMenuOpen);
  const isFocusedRef = useRef(false);
  const fragmentRef = useRef(fragment);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  dataRef.current = data;
  onChangeRef.current = onChange;
  isSlashMenuOpenRef.current = isSlashMenuOpen;
  fragmentRef.current = fragment;

  // Character offset of this instance's text within the whole paragraph
  const charStart = fragment?.charStart ?? 0;
  const isLastPart = !fragment || fragment.part === fragment.parts - 1;

  // Check if saved content is empty
  const isEmpty = !data.content || data.content.trim() === '' || data.content === '<br>';

  // Push the DOM content into state (unless the slash menu is using it as a search box)
  const commitContent = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    if (!contentRef.current) return;
    if (isSlashMenuOpenRef.current) return;

    let newContent = contentRef.current.innerHTML || '';
    // An emptied contenteditable keeps a placeholder <br>; that is not content
    if (!contentRef.current.textContent?.trim() && /^(\s|<br\s*\/?>)*$/i.test(newContent)) {
      newContent = '';
    }
    lastSetContentRef.current = newContent;

    if (newContent !== dataRef.current.content) {
      onChangeRef.current({
        ...dataRef.current,
        content: newContent
      });
    }
  }, []);

  // Does a pending caret record belong to this instance's text range?
  const ownsOffset = useCallback((offset: number): boolean => {
    const el = contentRef.current;
    if (!el) return false;
    const len = el.textContent?.length ?? 0;
    const start = fragmentRef.current?.charStart ?? 0;
    if (offset < start) return false;
    // The end of a non-final fragment belongs to the next fragment
    return isLastPart ? offset <= start + len : offset < start + len;
  }, [isLastPart]);

  // Set initial content and handle external content changes
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    // Always set content on initial mount or when block ID changes
    if (!initializedRef.current || blockIdRef.current !== block.id) {
      initializedRef.current = true;
      blockIdRef.current = block.id;

      lastSetContentRef.current = data.content || '';
      el.innerHTML = data.content || '';

      // If this block was just moved to another page while being edited, pick up
      // where the user left off: latest text (whole paragraphs only), focus and
      // caret position. Only an editable instance may claim it: the hidden
      // measurement copy renders the same block and must never consume it.
      const pending = isEditing ? peekCaret(block.id) : null;
      if (pending) {
        if (pending.html !== null && !fragment) {
          takeCaret(block.id);
          el.innerHTML = pending.html;
          lastSetContentRef.current = pending.html;
          el.classList.toggle('empty', !el.textContent?.trim());
          el.focus();
          setCaretOffset(el, pending.offset);
          if (pending.html !== (data.content || '')) {
            onChangeRef.current({ ...dataRef.current, content: pending.html });
          }
        } else if (pending.html === null && ownsOffset(pending.offset)) {
          takeCaret(block.id);
          el.focus();
          setCaretOffset(el, pending.offset - charStart);
        }
      }
      return;
    }

    // Only update DOM if content changed externally (not from user typing).
    // Compare canonical forms: a fragment re-sliced from the whole paragraph
    // serialises the same text slightly differently from innerHTML.
    const incoming = data.content || '';
    if (normaliseHtml(incoming) === normaliseHtml(lastSetContentRef.current)) {
      lastSetContentRef.current = incoming;
      return;
    }

    if (isFocusedRef.current) {
      // Keystrokes since the last commit live only in the DOM. Never overwrite
      // them: commit first, and let the next (fresh) slice reconcile.
      if (normaliseHtml(el.innerHTML || '') !== normaliseHtml(lastSetContentRef.current)) {
        commitContent();
        return;
      }
      // The text changed under the caret: a line migrated to or from the
      // neighbouring fragment while typing. Keep the caret on the same
      // character of the whole paragraph, handing it to the other fragment
      // if that character is no longer here.
      const localCaret = getCaretOffset(el) ?? (el.textContent?.length ?? 0);
      const globalCaret = charStart + localCaret;
      lastSetContentRef.current = incoming;
      el.innerHTML = incoming;
      if (ownsOffset(globalCaret)) {
        setCaretOffset(el, globalCaret - charStart);
      } else {
        stashCaretOffset(block.id, globalCaret);
      }
      return;
    }

    lastSetContentRef.current = incoming;
    el.innerHTML = incoming;
  }, [data.content, block.id, fragment, charStart, ownsOffset, isEditing, commitContent]);

  // Take over the caret when another instance of this paragraph handed it to
  // an offset that lives in this fragment (runs after every render). Never the
  // hidden measurement copy, which is not editable.
  useEffect(() => {
    if (!isEditing || isFocusedRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    const pending = peekCaret(block.id);
    if (pending && pending.html === null && ownsOffset(pending.offset)) {
      takeCaret(block.id);
      el.focus();
      setCaretOffset(el, pending.offset - charStart);
    }
  });

  // On unmount: commit anything still waiting in the debounce (e.g. the user
  // typed and closed the editor within 120ms), and if the block was focused
  // (it moved to another page mid-edit) stash the caret and DOM text so the
  // remounted block can restore them. A layout effect runs before React
  // detaches the ref and before the editor's own unmount flush.
  useLayoutEffect(() => {
    const blockId = block.id;
    const el = contentRef.current;
    return () => {
      const hadPendingCommit = commitTimerRef.current !== null;
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      if (el && isFocusedRef.current) {
        const frag = fragmentRef.current;
        stashCaret(blockId, el, frag?.charStart ?? 0, !frag);
      }
      if (hadPendingCommit && el && !isSlashMenuOpenRef.current) {
        const html = el.innerHTML || '';
        lastSetContentRef.current = html;
        if (html !== dataRef.current.content) {
          onChangeRef.current({ ...dataRef.current, content: html });
        }
      }
    };
  }, [block.id]);

  // Handle focus
  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  // Handle blur
  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    setIsFocused(false);
    setIsInSlashMode(false);
    commitContent();
    onBlur?.();
  }, [commitContent, onBlur]);

  // Handle keydown events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLParagraphElement>) => {
    // Handle "/" key to open command menu - check actual DOM content
    const domText = contentRef.current?.textContent || '';
    const isDomEmpty = !domText.trim();

    if (e.key === '/' && isDomEmpty && !isSlashMenuOpen) {
      // Don't prevent default - let the "/" be typed
      setIsInSlashMode(true);

      // Get position relative to the canvas for menu placement - above the block, left-aligned
      const rect = contentRef.current?.getBoundingClientRect();
      const blockWrapper = contentRef.current?.closest('.block-wrapper');
      const blockContent = blockWrapper?.querySelector('.block-content');
      const canvas = contentRef.current?.closest('.block-canvas');
      const canvasRect = canvas?.getBoundingClientRect();
      const blockContentRect = blockContent?.getBoundingClientRect();

      if (rect && canvasRect && blockContentRect && onSlashCommand) {
        // Position above the block, left-aligned with block content
        onSlashCommand({
          top: rect.top - canvasRect.top, // Top of the block
          left: blockContentRect.left - canvasRect.left
        });
      }
      return;
    }

    // Handle typing while in slash mode (for search) - let characters be typed naturally
    if (isSlashMenuOpen && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      // Don't prevent default - let the character be typed
      // Update search query based on what will be typed
      onSlashSearch?.(slashSearchQuery + e.key);
      return;
    }

    // Handle backspace in slash mode
    if (isSlashMenuOpen && e.key === 'Backspace') {
      // Don't prevent default - let backspace happen naturally
      if (slashSearchQuery.length > 0) {
        onSlashSearch?.(slashSearchQuery.slice(0, -1));
      } else {
        // User is backspacing the `/` - close the menu
        const currentText = contentRef.current?.textContent || '';
        if (currentText === '/') {
          onSlashClose?.();
        }
      }
      return;
    }

    // Handle Enter key: split the paragraph at the caret
    if (e.key === 'Enter' && !e.shiftKey) {
      // If slash menu is open, let it handle the enter
      if (isSlashMenuOpen) {
        return;
      }

      e.preventDefault();
      const el = contentRef.current;
      if (!el) return;

      let before = el.innerHTML || '';
      let after = '';
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && el.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        const beforeRange = document.createRange();
        beforeRange.selectNodeContents(el);
        beforeRange.setEnd(range.startContainer, range.startOffset);
        const afterRange = document.createRange();
        afterRange.selectNodeContents(el);
        afterRange.setStart(range.endContainer, range.endOffset);
        before = trimTrailingBreak(rangeToHtml(beforeRange));
        after = trimTrailingBreak(rangeToHtml(afterRange));
      }

      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      // Keep the DOM in step with what we hand to the parent
      el.innerHTML = before;
      lastSetContentRef.current = before;
      // Replacing innerHTML drops the caret to the START of this block, where
      // it showed until the new block took focus -- a visible jump to the
      // front of the line and back down. Put it back at the end of what
      // stays, which is where Enter was pressed.
      if (selection) {
        const caret = document.createRange();
        caret.selectNodeContents(el);
        caret.collapse(false);
        selection.removeAllRanges();
        selection.addRange(caret);
      }
      onEnter?.(before, after);
      return;
    }

    // Moving across the page break of a split paragraph
    if (fragment) {
      const el = contentRef.current;
      const selection = window.getSelection();
      const collapsed = !!selection && selection.isCollapsed;
      const caret = el ? getCaretOffset(el) : null;
      const textLen = el?.textContent?.length ?? 0;
      if (collapsed && caret === 0 && fragment.part > 0 && (e.key === 'Backspace' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        commitContent();
        focusSiblingFragment(block.id, fragment.part - 1, true);
        return;
      }
      if (collapsed && caret === textLen && !isLastPart && e.key === 'ArrowRight') {
        e.preventDefault();
        commitContent();
        focusSiblingFragment(block.id, fragment.part + 1, false);
        return;
      }
    }

    // Handle Backspace on empty content - check actual DOM content
    const currentText = contentRef.current?.textContent || '';
    if (e.key === 'Backspace' && !currentText.trim()) {
      e.preventDefault();
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      if (fragment) {
        // Only this page's piece is empty; the rest of the paragraph lives on
        // another page. Merge the empty piece and move to the previous piece
        // instead of deleting the whole block.
        commitContent();
        if (fragment.part > 0) focusSiblingFragment(block.id, fragment.part - 1, true);
        return;
      }
      onBackspaceEmpty?.();
      return;
    }
  }, [isSlashMenuOpen, slashSearchQuery, onSlashCommand, onSlashSearch, onSlashClose, onEnter, onBackspaceEmpty, fragment, isLastPart, block.id, commitContent]);

  // Handle input for real-time content tracking
  const handleInput = useCallback(() => {
    // Toggle 'empty' class directly on DOM to avoid re-render that resets content
    if (contentRef.current) {
      const text = contentRef.current.textContent || '';
      if (text.trim()) {
        contentRef.current.classList.remove('empty');
      } else {
        contentRef.current.classList.add('empty');
      }
    }

    // Exit slash mode if user types regular content
    if (isInSlashMode && !isSlashMenuOpen) {
      setIsInSlashMode(false);
    }

    // Commit shortly after typing pauses so the page reflows and nothing is
    // left uncommitted if the editor closes.
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(commitContent, COMMIT_DELAY_MS);
  }, [isInSlashMode, isSlashMenuOpen, commitContent]);


  // Handle paste - strip formatting and detect bullet patterns
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLParagraphElement>) => {
    e.preventDefault();

    // Get plain text from clipboard
    const plainText = e.clipboardData.getData('text/plain');

    if (!plainText) return;

    // Parse the pasted text to detect structure
    const parsedBlocks = parsePastedText(plainText);

    // If we have multiple blocks or bullet lists, use the multi-block handler
    if (parsedBlocks.length > 1 || (parsedBlocks.length === 1 && parsedBlocks[0].type === 'bullet_list')) {
      if (onPasteMultiBlock) {
        if (commitTimerRef.current) {
          clearTimeout(commitTimerRef.current);
          commitTimerRef.current = null;
        }
        const currentContent = contentRef.current?.innerHTML || '';
        lastSetContentRef.current = currentContent;
        onPasteMultiBlock(parsedBlocks, currentContent);
        return;
      }
    }

    // For simple single paragraph paste, insert as plain text
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      // Insert plain text
      const textNode = document.createTextNode(plainText.replace(/\n/g, ' '));
      range.insertNode(textNode);

      // Move cursor to end of inserted text
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);

      // Trigger input handler
      handleInput();
    }
  }, [onPasteMultiBlock, handleInput]);

  // When the slash menu closes without a selection: a lone "/" was only ever
  // the menu trigger, so clear it (otherwise the paragraph is no longer empty
  // and the menu cannot be opened again); anything more (e.g. "/3 of the
  // beams") is real text and is kept and saved.
  useEffect(() => {
    if (!isSlashMenuOpen && isInSlashMode) {
      setIsInSlashMode(false);
      const el = contentRef.current;
      if (el && (el.textContent || '').trim() === '/') {
        el.textContent = '';
        el.classList.add('empty');
      }
      commitContent();
    }
  }, [isSlashMenuOpen, isInSlashMode, commitContent]);

  // Determine placeholder text
  const getPlaceholder = () => {
    if (isSlashMenuOpen) {
      // Don't show placeholder when slash menu is open - text is actually typed
      return '';
    }
    if (isFocused && isEmpty) {
      return "Type '/' for commands";
    }
    // Empty unfocused blocks show no placeholder (cleaner look)
    return '';
  };

  return (
    <div className={`paragraph-block ${isFocused ? 'focused' : ''} ${isSlashMenuOpen ? 'slash-mode' : ''}`}>
      <p
        ref={contentRef}
        className={`paragraph-content ${isEmpty ? 'empty' : ''}`}
        contentEditable={isEditing}
        suppressContentEditableWarning
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={getPlaceholder()}
      />
    </div>
  );
};

export default ParagraphBlock;
